<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  rootTag: {
    type: String,
    default: 'form',
    validator: value => ['form', 'div'].includes(value),
  },
  saving: { type: Boolean, default: false },
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  response: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();

const formState = {
  uiFlags: useMapGetter('captainResponses/getUIFlags'),
};

const initialState = {
  question: '',
  answer: '',
};

const state = reactive({ ...initialState });
const baseline = ref(JSON.stringify(state));
const isDirty = computed(() => JSON.stringify(state) !== baseline.value);

const validationRules = {
  question: { required, minLength: minLength(1) },
  answer: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(
  () =>
    props.saving ||
    formState.uiFlags.value[
      props.mode === 'edit' ? 'updatingItem' : 'creatingItem'
    ]
);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.RESPONSES.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  question: getErrorMessage('question', 'QUESTION'),
  answer: getErrorMessage('answer', 'ANSWER'),
}));

const handleCancel = () => emit('cancel');

const prepareDocumentDetails = () => ({
  question: state.question,
  answer: state.answer,
});

const handleSubmit = async () => {
  if (isLoading.value) return;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || isLoading.value) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};

const updateStateFromResponse = response => {
  if (!response) return;

  const { question = '', answer = '' } = response;

  Object.assign(state, {
    question,
    answer,
  });
};

watch(
  () => props.response,
  newResponse => {
    if (props.mode === 'edit' && newResponse) {
      updateStateFromResponse(newResponse);
    } else {
      Object.assign(state, initialState);
    }
    baseline.value = JSON.stringify(state);
    v$.value.$reset();
  },
  { immediate: true }
);
const handleFieldEnter = event => {
  if (props.rootTag === 'form' || event.isComposing || event.defaultPrevented)
    return;
  event.preventDefault();
  handleSubmit();
};
defineExpose({ isDirty, submit: handleSubmit });
</script>

<template>
  <component
    :is="rootTag"
    class="flex flex-col gap-4"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.question"
      :disabled="isLoading"
      :custom-input-class="isWorkspaceV2 ? '!h-11' : ''"
      :label="t('CAPTAIN.RESPONSES.FORM.QUESTION.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.QUESTION.PLACEHOLDER')"
      :message="formErrors.question"
      :message-type="formErrors.question ? 'error' : 'info'"
      @keydown.enter="handleFieldEnter"
    />
    <Editor
      v-model="state.answer"
      :disabled="isLoading"
      :editor-label="t('CAPTAIN.RESPONSES.FORM.ANSWER.LABEL')"
      :label="t('CAPTAIN.RESPONSES.FORM.ANSWER.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.ANSWER.PLACEHOLDER')"
      :message="formErrors.answer"
      :max-length="10000"
      :message-type="formErrors.answer ? 'error' : 'info'"
    />
    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        :disabled="isLoading"
        :class="{ '!h-11': isWorkspaceV2 }"
        @click="handleCancel"
      />
      <Button
        :type="rootTag === 'form' ? 'submit' : 'button'"
        :class="{ '!h-11': isWorkspaceV2 }"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="rootTag !== 'form' && handleSubmit()"
      />
    </div>
  </component>
</template>
